<template>
<!-- eslint-disable max-len -->
<div class="container">
<div class="row no-gutters">
<div class="login-form col-sm-12">
  <h5>Data Table</h5>
  <div style="whitespace: pre-wrap; font-size: 85%; margin-bottom: 10px;">
    If not all playthroughs are marked complete, note that recent progress may not show up in this data until later, as the data takes time to process.
    <br>Click on column title to sort by column from high to low and from low to high.
    <br>Click on the red &times; in the column header to remove the column from the table.
    <br>Click on left and right arrows in the column header to reorder that column in the table.
    <br>Scroll the table left and right via the trackpad, middle mouse button, or the left and right arrow keys.
  </div>
  <button class="btn" @click.prevent="exportToCSV" style="margin-bottom: 15px; padding: 0 5px;">
    Export To CSV
  </button>
  <!-- {{dataArray}} -->
  <div>Total Count: {{dataArray.length}}</div>
  <b-input-group>
    <b-input-group-prepend style="margin: 5px 5px 5px 0;">
      Filter:
    </b-input-group-prepend>
    <b-input v-model="filter"/>
  </b-input-group>
  <div>Filtered Count: {{filteredCount}}</div>
  <div style="width: 100%; font-size: 12px; overflow-x: auto; margin-bottom: 2px;" tabindex="0" ref="tableScrollView">
    <b-table
      :filter="filter"
      :items="dataArray"
      :fields="tableFields"
      :current-page="currentPage"
      :per-page="perPage"
      striped
      hover
      ref="table"
      style="margin-bottom: 0;"
    >
      <template slot="head()" slot-scope="data">
        <div style="display: flex; flex-direction: column;">
          <strong>{{ data.label }}</strong>
          <div style="display: flex; flex-direction: row; margin-top: 1px;">
            <button class="btn-small" style="background-color: rgb(230, 30, 30); color: white;" @click.prevent="removeColumn(data)">&times;</button>
            <button class="btn-small" @click.prevent="shiftColumnLeft(data)">&lt;</button>
            <button class="btn-small" @click.prevent="shiftColumnRight(data)">&gt;</button>
          </div>
        </div>
      </template>
    </b-table>
  </div>
  <nav>
    <b-pagination
      size="sm"
      :total-rows="filteredCount"
      :per-page="perPage"
      v-model="currentPage"
      prev-text="Prev"
      next-text="Next"
    />
  </nav>
  <b-input-group style="width: 30%; font-size: 80%;">
    <b-input-group-prepend style="margin: 8px 5px 5px 0;">
      Rows per page:
    </b-input-group-prepend>
    <b-input v-model="perPageInput" style="transform: scaleY(0.84); margin-bottom: 5px;"/>
    <b-input-group-append>
      <button class="btn" @click.prevent="updatePerPage" style="transform: scale(0.82) translate(-9%, -2%); margin-bottom: 5px;">
        Update
      </button>
    </b-input-group-append>
  </b-input-group>
</div>
</div>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FileSaver from 'file-saver';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import SelectInput from '../../ui/inputs/SelectInput';

import { objectToCSV } from '../../../utils/csv-util';

export default {
  components: {
    ArrayInput,
    CheckboxInput,
    SelectInput
  },
  data() {
    return {
      exporting: false,
      currentPage: 1,
      perPage: 25,
      perPageInput: 25,
      filter: '',
      tableFields: [],
      tableFocused: false
    };
  },
  created() {
    window.scrollTo(0, 0);
    window.addEventListener('keydown', this.focusTableIfLeftOrRight);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.focusTableIfLeftOrRight);
  },
  mounted() {
    this.initTableFields();
  },
  computed: {
    ...mapGetters([
      'tableData',
      'exportFileName',
      'defaultSortFunction'
    ]),
    indexMap() {
      if (this.tableData == null || this.tableData.cols == null) {
        return new Map();
      }
      const m = new Map();
      let i = 0;
      // eslint-disable-next-line no-plusplus
      this.tableData.cols.forEach(el => m.set(el, i++));
      return m;
    },
    // unpacks the data tree into a plain array of data rows
    dataArray() {
      if (this.tableData == null || this.tableData.rows == null) {
        return [];
      }
      const result = [];
      const rowsToProcess = [];
      const outputRow = {};
      for (let i = this.tableData.rows.length - 1; i >= 0; --i) {
        // iterate backward to maintain order when popped
        // TODO: check that this is faster than a single `push(...(rows.reverse()))`
        rowsToProcess.push(this.tableData.rows[i]);
      }
      while (rowsToProcess.length > 0) {
        const row = rowsToProcess.pop(); // LIFO for depth first traversal
        // eslint-disable-next-line no-loop-func
        row.vals.forEach((val) => {
          outputRow[val.c] = val.v;
        });
        // eslint-disable-next-line eqeqeq
        if (row.rows != undefined) {
          for (let j = row.rows.length - 1; j >= 0; --j) {
            // iterate backward to maintain order when popped
            // TODO: check that this is faster than a single `push(...(rows.reverse()))`
            rowsToProcess.push(row.rows[j]);
          }
        } else {
          result.push(Object.assign({}, outputRow)); // copy so it's a separate reference
        }
      }
      if (this.defaultSortFunction != null) {
        result.sort(this.defaultSortFunction);
      }
      return result;
    },
    filteredCount() {
      return (this.filter !== '' && this.$refs.table && this.$refs.table.sortedItems
        && this.$refs.table.sortedItems.length != null)
        ? this.$refs.table.sortedItems.length
        : this.dataArray.length;
    },
    editedTableData() {
      return {
        cols: this.tableFields.map(el => el.label),
        rows: this.$refs.table.sortedItems.map((el) => {
          const row = { vals: [] };
          let col = 0;
          const matchCol = (x => x.key === `${col}`);
          while (col < this.tableData.cols.length) {
            const newCol = this.tableFields.findIndex(matchCol);
            if (newCol >= 0) {
              row.vals.push({ c: newCol, v: ((el[col] != null) ? el[col] : '') });
            }
            col += 1;
          }
          return row;
        })
      };
    }
  },
  methods: {
    ...mapActions([
      'flashPrimary',
      'flashSuccess',
      'flashDanger',
      'getUsageData'
    ]),
    exportToCSV() {
      if (this.exporting) return;
      this.exporting = true;

      try {
        this.flashSuccess({ message: 'Data exporting to CSV...' });

        const csvString = objectToCSV(this.editedTableData);

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
        FileSaver.saveAs(blob, this.exportFileName || 'data.csv');

        this.$nextTick(() => { this.exporting = false; }); // lets you download again if you want
      } catch (_) {
        const message = 'Error downloading data, please try again';
        this.flashDanger({ message });
        this.exporting = false;
      }
    },
    updatePerPage() {
      this.perPage = this.perPageInput;
    },
    initTableFields() {
      if (this.tableData == null || this.tableData.cols == null) {
        return [];
      }
      this.tableFields = this.tableData.cols.map(el => ({
        key: `${this.indexMap.get(el)}`,
        label: el, // use raw column headers for the actual display label
        sortable: true
      }));
    },
    removeColumn(data) {
      this.tableFields = this.tableFields.filter(el => el.key !== data.field.key);
      if (this.tableFields.length === 0) {
        this.initTableFields();
      }
    },
    shiftColumnLeft(data) {
      const index = this.tableFields.findIndex(el => el.key === data.field.key);
      const destination = ((index - 1) < 0) ? (this.tableFields.length - 1) : index - 1;
      const f = this.tableFields.splice(index, 1)[0];
      this.tableFields.splice(destination, 0, f);
    },
    shiftColumnRight(data) {
      const index = this.tableFields.findIndex(el => el.key === data.field.key);
      const destination = ((index + 1) > (this.tableFields.length - 1)) ? 0 : index + 1;
      const f = this.tableFields.splice(index, 1)[0];
      this.tableFields.splice(destination, 0, f);
    },
    focusTableIfLeftOrRight(ev) {
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowLeft') {
        const x = window.scrollX;
        const y = window.scrollY;
        this.$refs.tableScrollView.focus();
        window.scrollTo(x, y);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.login-form {
  background-color: white;
  margin-top: 50px;
  padding: 20px;
}

.btn-small {
  width: 14px;
  height: 14px;
  border: solid black 1px;
  border-radius: 3px;
  font-size: 10px;
  text-align: center;
  padding: 0;
  margin-right: 1px;
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/DataTableView.vue